import { useEffect, useState } from 'react'
import {
  readDraft,
  readSignupCommunityLogoDataUrl,
  readSignupVenuePhotos,
} from '../components/signup/signupState'

/**
 * Read setup-mode + venue data for the venue persona.
 *
 * FE-only: backed by sessionStorage signup draft + localStorage logo/
 * photos. Mirrors useSetupMode (the community variant) but derives
 * setup mode purely from draft.venue presence + the bookedAt flag, NOT
 * from accountStatus. (The venue wizard never sets accountStatus, and
 * the spec defines venue setup mode by venue draft existence.)
 *
 * Why a separate hook? Three reasons:
 *   1. The trigger conditions are different (venue is bookedAt-driven,
 *      community is accountStatus-driven).
 *   2. The shape of "venueData" surfaced to consumers (logo, photos,
 *      courts, address) is venue-specific — separating keeps callers
 *      from poking at the wrong fields by accident.
 *   3. The `_tick` re-render dance and storage event subscription
 *      stay scoped to changes that affect this hook's outputs.
 *
 * Returns:
 *   {
 *     isSetupMode  — true when a venue draft exists AND no bookedAt
 *     hasDraftVenue — true if draft.venue.name is set (used by the
 *       layout/route guard to decide whether to bypass Supabase even
 *       in active mode, since active mode is also FE-only today)
 *     bookedAt      — ISO string or '' (set by Welcome's Calendly
 *       postMessage handler)
 *     venueData     — { name, logo, photos, courts, country,
 *                       countryCode, address, sportType }
 *     fullName      — signup user name (for sidebar / account block)
 *     refresh()     — re-read from storage (for after a write in
 *       same tab)
 *   }
 *
 * BACKEND TODO: replace with a hook that reads
 *   Users.account_status + Users.venue_id + venues.activation_status
 *   via Supabase. Today's FE-only flow has no backend persistence
 *   so the source of truth is sessionStorage + localStorage.
 */
export default function useVenueSetupMode() {
  const [tick, setTick] = useState(0)

  // Re-read on every render (cheap — sessionStorage parse + tiny obj
  // assemble). Subscribe to cross-tab storage events so a change in
  // another tab also re-renders.
  const draft = readDraft()
  const venue = draft.venue || {}
  const venueName = (venue.name || '').trim()
  const bookedAt = venue.bookedAt || ''
  const hasDraftVenue = !!venueName
  const isSetupMode = hasDraftVenue && !bookedAt

  useEffect(() => {
    const onStorage = (e) => {
      // Match the same key prefix the signup state writer uses, plus
      // the localStorage logo / photos keys that affect venueData.
      if (
        !e.key ||
        e.key.startsWith('statbox.signupDraft') ||
        e.key === 'statbox_logo_data_url' ||
        e.key === 'statbox.community.logo.v1' ||
        e.key === 'statbox_venue_photos'
      ) {
        setTick((t) => t + 1)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // venueData snapshot. Always returned (even outside setup mode) so
  // consumers can read the user's chosen identity to show in the
  // post-activation portal too — until the backend has its own
  // canonical venue row.
  const venueData = {
    name: venueName,
    logo: readSignupCommunityLogoDataUrl() || '',
    photos: readSignupVenuePhotos(),
    courts: Array.isArray(venue.courts) ? venue.courts : [],
    country: venue.country || '',
    countryCode: venue.address?.countryCode || venue.country || '',
    address: venue.address || null,
    sportType: venue.sportType || '',
  }

  return {
    isSetupMode,
    hasDraftVenue,
    bookedAt,
    venueData,
    fullName: draft.fullName || '',
    email: draft.email || '',
    venueId: draft.venueId || null,
    refresh: () => setTick((t) => t + 1),
    _tick: tick,
  }
}
